import React, { useState, useRef, useEffect, useCallback } from 'react';
import LanguageContext from '../helper/LanguageContext';
import './VoiceMessagePlayer.css';

// Static voice waveform of the mobile Pro chat (proChatKit VOICE_BARS): 30 bars, 4…24.
const VOICE_BARS = [6, 10, 14, 9, 18, 22, 15, 11, 19, 24, 17, 12, 8, 14, 20, 23, 16, 10, 7, 12, 18, 21, 14, 9, 13, 17, 11, 8, 12, 6];
// Full-width bubble: the same shape twice (mirrored), so the bars stay thin across the width.
const VOICE_BARS_WIDE = VOICE_BARS.concat(VOICE_BARS.slice().reverse());
const SPEEDS = [1, 1.5, 2];
const speedLabel = (s) => `${String(s).replace('.', ',')}×`;

const formatTime = (seconds) => {
  if (!isFinite(seconds) || isNaN(seconds) || seconds < 0) return '0:00';
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
};

/**
 * Voice message player (mobile ProMediaBlocks.ProVoiceRow / RNAudioPlayer): a 40 round
 * accent play / pause, a 30-bar waveform that shows the progress and seeks on click or
 * drag, the time (m:ss) and the speed chip 1× → 1,5× → 2× (playbackRate).
 * Same props as before: src, audioType, onPlay, onPause; `wide` fills its container
 * (60 bars) instead of the compact 240 row.
 */
const VoiceMessagePlayer = ({ src, audioType = 'audio/wav', onPlay, onPause, wide = false }) => {
  const { strings, isGerm } = React.useContext(LanguageContext);
  const audioRef = useRef(null);
  const waveRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [speed, setSpeed] = useState(1);
  const prevSrcRef = useRef(src);

  const t = (key, de, en) => (strings && strings[key]) || (isGerm ? de : en);
  const bars = wide ? VOICE_BARS_WIDE : VOICE_BARS;

  // Reload audio element when src changes (e.g. base64 → server URL)
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    if (prevSrcRef.current !== src) {
      prevSrcRef.current = src;
      audio.load();
    }
  }, [src]);

  // The chosen speed survives reloads and plays.
  useEffect(() => {
    const audio = audioRef.current;
    if (audio) audio.playbackRate = speed;
  }, [speed]);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return undefined;

    const setDurationSafe = () => {
      const d = audio.duration;
      if (d && isFinite(d) && d > 0) setDuration(d);
      audio.playbackRate = speed;
    };
    const handleTimeUpdate = () => {
      if (!isDragging) setCurrentTime(audio.currentTime || 0);
    };
    const handleEnded = () => {
      setIsPlaying(false);
      setCurrentTime(0);
      if (onPause) onPause();
    };
    const handlePlay = () => {
      audio.playbackRate = speed;
      setIsPlaying(true);
      if (onPlay) onPlay();
    };
    const handlePause = () => {
      setIsPlaying(false);
      if (onPause) onPause();
    };

    audio.addEventListener('loadedmetadata', setDurationSafe);
    audio.addEventListener('durationchange', setDurationSafe);
    audio.addEventListener('timeupdate', handleTimeUpdate);
    audio.addEventListener('ended', handleEnded);
    audio.addEventListener('play', handlePlay);
    audio.addEventListener('pause', handlePause);
    if (audio.readyState >= 1) setDurationSafe();

    return () => {
      audio.removeEventListener('loadedmetadata', setDurationSafe);
      audio.removeEventListener('durationchange', setDurationSafe);
      audio.removeEventListener('timeupdate', handleTimeUpdate);
      audio.removeEventListener('ended', handleEnded);
      audio.removeEventListener('play', handlePlay);
      audio.removeEventListener('pause', handlePause);
    };
  }, [isDragging, onPlay, onPause, speed]);

  const togglePlayPause = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const audio = audioRef.current;
    if (!audio) return;
    if (audio.paused) {
      audio.play().catch((err) => {
        console.error('Error playing audio:', err);
      });
    } else {
      audio.pause();
    }
  };

  const seekTo = useCallback((clientX) => {
    const audio = audioRef.current;
    const wave = waveRef.current;
    if (!audio || !wave || !duration) return;
    const rect = wave.getBoundingClientRect();
    const ratio = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
    const next = ratio * duration;
    audio.currentTime = next;
    setCurrentTime(next);
  }, [duration]);

  const onWaveMouseDown = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
    seekTo(e.clientX);
  };

  useEffect(() => {
    if (!isDragging) return undefined;
    const onMove = (e) => seekTo(e.clientX);
    const onUp = () => setIsDragging(false);
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
    return () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
    };
  }, [isDragging, seekTo]);

  const onWaveKeyDown = (e) => {
    const audio = audioRef.current;
    if (!audio || !duration) return;
    if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
    e.preventDefault();
    const next = Math.max(0, Math.min(duration, (audio.currentTime || 0) + (e.key === 'ArrowRight' ? 5 : -5)));
    audio.currentTime = next;
    setCurrentTime(next);
  };

  const cycleSpeed = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setSpeed((s) => SPEEDS[(SPEEDS.indexOf(s) + 1) % SPEEDS.length]);
  };

  const ratio = duration > 0 ? Math.max(0, Math.min(1, currentTime / duration)) : 0;
  const started = isPlaying || currentTime > 0;
  const playLabel = isPlaying
    ? t('chat_voice_pause', 'Pause', 'Pause')
    : t('chat_voice_play', 'Sprachnachricht abspielen', 'Play voice message');
  const speedName = t('chat_voice_speed', 'Geschwindigkeit', 'Speed');

  // Divs, not spans: chat bubbles force their own font size and colour onto spans.
  return (
    <div className={`kjc-voice${wide ? ' kjc-voice--wide' : ''}`} onClick={(e) => e.stopPropagation()}>
      <button
        type="button"
        className="kj-btn kj-btn--icon kj-btn--round kj-btn--primary kjc-voice__play"
        onClick={togglePlayPause}
        aria-label={playLabel}
        title={playLabel}
      >
        <div className="material-symbols-outlined kjc-voice__play-icon">{isPlaying ? 'pause' : 'play_arrow'}</div>
      </button>
      <div className="kjc-voice__body">
        <div
          ref={waveRef}
          className="kjc-voice__wave"
          role="slider"
          tabIndex={0}
          aria-label={playLabel}
          aria-valuemin={0}
          aria-valuemax={Math.round(duration) || 0}
          aria-valuenow={Math.round(currentTime) || 0}
          aria-valuetext={`${formatTime(currentTime)} / ${formatTime(duration)}`}
          onMouseDown={onWaveMouseDown}
          onKeyDown={onWaveKeyDown}
        >
          {bars.map((h, i) => (
            <div
              key={i}
              className={`kjc-voice__bar kjc-voice__bar--h${h}${(i + 0.5) / bars.length <= ratio ? ' is-played' : ''}`}
            />
          ))}
        </div>
        <div className="kjc-voice__foot">
          <div className="kjc-voice__time">{formatTime(started ? currentTime : duration)}</div>
          <div
            className="kjc-voice__speed"
            role="button"
            tabIndex={0}
            onClick={cycleSpeed}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') cycleSpeed(e);
            }}
            aria-label={`${speedName} ${speedLabel(speed)}`}
            title={speedName}
          >
            {speedLabel(speed)}
          </div>
        </div>
      </div>
      <audio ref={audioRef} preload="metadata">
        <source src={src} type={audioType} />
      </audio>
    </div>
  );
};

export default VoiceMessagePlayer;
